// Pricing1.js - Contains functions for creating a pricing section
import { v4 as uuidv4 } from 'uuid';
import { DEVICES } from '../../constants/breakpoints';

/**
 * Creates a Pricing section with multiple pricing tiers
 * @param {function} getHierarchyName - Function to get hierarchy name for elements
 * @param {string} currentPageId - The current page ID where the pricing section is being added
 * @param {function} setClassStylesRegistry - Function to update class styles registry
 * @param {function} setGlobalClassNames - Function to update global class names
 * @returns {Object} - The pricing section elements
 */
export const createPricingElements = (
  getHierarchyName, 
  currentPageId,
  setClassStylesRegistry,
  setGlobalClassNames
) => {
  // Define class names
  const pricingSectionClass = 'pricing1-section';
  const pricingContainerClass = 'pricing1-container';
  const pricingHeaderClass = 'pricing1-header';
  const pricingTitleClass = 'pricing1-title';
  const pricingSubtitleClass = 'pricing1-subtitle';
  const pricingToggleClass = 'pricing1-toggle';
  const pricingToggleButtonClass = 'pricing1-toggle-button';
  const pricingToggleActiveClass = 'pricing1-toggle-active';
  const pricingCardsClass = 'pricing1-cards';
  const pricingCardClass = 'pricing1-card';
  const pricingCardPopularClass = 'pricing1-card-popular';
  const pricingCardHeaderClass = 'pricing1-card-header';
  const pricingCardTitleClass = 'pricing1-card-title';
  const pricingCardPriceClass = 'pricing1-card-price';
  const pricingCardPeriodClass = 'pricing1-card-period';
  const pricingCardFeaturesClass = 'pricing1-card-features';
  const pricingCardFeatureClass = 'pricing1-card-feature';
  const pricingCardButtonClass = 'pricing1-card-button';
  const pricingCardPopularTagClass = 'pricing1-card-popular-tag';
  
  // Desktop Styles (default)
  const pricingSectionStyles = {
    position: 'relative',
    paddingTop: '80px',
    paddingBottom: '80px',
    paddingLeft: '20px',
    paddingRight: '20px',
    backgroundColor: '#f8f9fa',
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center'
  };

  const pricingContainerStyles = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    maxWidth: '1200px',
    width: '100%',
    marginLeft: 'auto',
    marginRight: 'auto',
    gap: '60px'
  };

  const pricingHeaderStyles = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    marginBottom: '20px',
    width: '100%',
    maxWidth: '700px'
  };

  const pricingTitleStyles = {
    fontSize: '36px',
    fontWeight: 'bold',
    color: '#333333',
    marginBottom: '16px'
  };

  const pricingSubtitleStyles = {
    fontSize: '18px',
    color: '#666666',
    lineHeight: '1.6',
    marginBottom: '30px'
  };

  const pricingToggleStyles = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '15px',
    marginTop: '20px'
  };

  const pricingToggleButtonStyles = {
    padding: '8px 16px',
    borderRadius: '20px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    backgroundColor: 'transparent',
    color: '#666666',
    border: 'none'
  };

  const pricingToggleActiveStyles = {
    backgroundColor: '#4361ee',
    color: '#ffffff'
  };

  const pricingCardsStyles = {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'center',
    gap: '30px',
    width: '100%'
  };

  const pricingCardStyles = {
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: '#ffffff',
    borderRadius: '10px',
    boxShadow: '0 5px 20px rgba(0,0,0,0.1)',
    padding: '40px 30px',
    flex: '1',
    minWidth: '300px',
    maxWidth: '350px',
    position: 'relative',
    transition: 'transform 0.3s ease, box-shadow 0.3s ease'
  };

  const pricingCardPopularStyles = {
    transform: 'scale(1.05)',
    boxShadow: '0 8px 30px rgba(0,0,0,0.15)',
    border: '2px solid #4361ee'
  };

  const pricingCardHeaderStyles = {
    marginBottom: '30px',
    textAlign: 'center'
  };

  const pricingCardTitleStyles = {
    fontSize: '24px',
    fontWeight: 'bold',
    color: '#333333',
    marginBottom: '16px'
  };

  const pricingCardPriceStyles = {
    fontSize: '48px',
    fontWeight: 'bold',
    color: '#4361ee',
    marginBottom: '8px',
    lineHeight: '1'
  };

  const pricingCardPeriodStyles = {
    fontSize: '16px',
    color: '#666666'
  };

  const pricingCardFeaturesStyles = {
    display: 'flex',
    flexDirection: 'column',
    gap: '15px',
    marginBottom: '30px',
    flex: '1'
  };

  const pricingCardFeatureStyles = {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    fontSize: '16px',
    color: '#666666'
  };

  const pricingCardButtonStyles = {
    padding: '12px 24px',
    borderRadius: '5px',
    fontSize: '16px',
    fontWeight: '500',
    cursor: 'pointer',
    textAlign: 'center',
    transition: 'all 0.3s ease',
    marginTop: 'auto'
  };

  const pricingCardPopularTagStyles = {
    position: 'absolute',
    top: '15px',
    right: '15px',
    backgroundColor: '#4361ee',
    color: '#ffffff',
    padding: '5px 10px',
    fontSize: '12px',
    fontWeight: 'bold',
    borderRadius: '5px'
  };

  // Tablet Styles
  const tabletPricingCardsStyles = {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: '20px'
  };

  const tabletPricingCardStyles = {
    minWidth: '280px',
    maxWidth: '320px'
  };

  // Mobile Styles
  const mobilePricingCardsStyles = {
    flexDirection: 'column',
    alignItems: 'center',
    width: '100%'
  };

  const mobilePricingCardStyles = {
    width: '100%',
    maxWidth: '350px',
    minWidth: '250px',
    margin: '0 auto'
  };

  const mobilePricingCardPopularStyles = {
    transform: 'scale(1.02)'
  };

  // Update the classStylesRegistry
  setClassStylesRegistry(prev => {
    const newRegistry = { ...prev };
    
    // Desktop styles
    newRegistry[DEVICES.DESKTOP].set(pricingSectionClass, pricingSectionStyles);
    newRegistry[DEVICES.DESKTOP].set(pricingContainerClass, pricingContainerStyles);
    newRegistry[DEVICES.DESKTOP].set(pricingHeaderClass, pricingHeaderStyles);
    newRegistry[DEVICES.DESKTOP].set(pricingTitleClass, pricingTitleStyles);
    newRegistry[DEVICES.DESKTOP].set(pricingSubtitleClass, pricingSubtitleStyles);
    newRegistry[DEVICES.DESKTOP].set(pricingToggleClass, pricingToggleStyles);
    newRegistry[DEVICES.DESKTOP].set(pricingToggleButtonClass, pricingToggleButtonStyles);
    newRegistry[DEVICES.DESKTOP].set(pricingToggleActiveClass, pricingToggleActiveStyles);
    newRegistry[DEVICES.DESKTOP].set(pricingCardsClass, pricingCardsStyles);
    newRegistry[DEVICES.DESKTOP].set(pricingCardClass, pricingCardStyles);
    newRegistry[DEVICES.DESKTOP].set(pricingCardPopularClass, pricingCardPopularStyles);
    newRegistry[DEVICES.DESKTOP].set(pricingCardHeaderClass, pricingCardHeaderStyles);
    newRegistry[DEVICES.DESKTOP].set(pricingCardTitleClass, pricingCardTitleStyles);
    newRegistry[DEVICES.DESKTOP].set(pricingCardPriceClass, pricingCardPriceStyles);
    newRegistry[DEVICES.DESKTOP].set(pricingCardPeriodClass, pricingCardPeriodStyles);
    newRegistry[DEVICES.DESKTOP].set(pricingCardFeaturesClass, pricingCardFeaturesStyles);
    newRegistry[DEVICES.DESKTOP].set(pricingCardFeatureClass, pricingCardFeatureStyles);
    newRegistry[DEVICES.DESKTOP].set(pricingCardButtonClass, pricingCardButtonStyles);
    newRegistry[DEVICES.DESKTOP].set(pricingCardPopularTagClass, pricingCardPopularTagStyles);

    // Tablet styles
    newRegistry[DEVICES.TABLET].set(pricingCardsClass, tabletPricingCardsStyles);
    newRegistry[DEVICES.TABLET].set(pricingCardClass, tabletPricingCardStyles);

    // Mobile styles
    newRegistry[DEVICES.MOBILE].set(pricingCardsClass, mobilePricingCardsStyles);
    newRegistry[DEVICES.MOBILE].set(pricingCardClass, mobilePricingCardStyles);
    newRegistry[DEVICES.MOBILE].set(pricingCardPopularClass, mobilePricingCardPopularStyles);

    return newRegistry;
  });

  // Add classes to globalClassNames
  setGlobalClassNames(prev => new Set([
    ...prev,
    pricingSectionClass,
    pricingContainerClass,
    pricingHeaderClass,
    pricingTitleClass,
    pricingSubtitleClass,
    pricingToggleClass,
    pricingToggleButtonClass,
    pricingToggleActiveClass,
    pricingCardsClass,
    pricingCardClass,
    pricingCardPopularClass,
    pricingCardHeaderClass,
    pricingCardTitleClass,
    pricingCardPriceClass,
    pricingCardPeriodClass,
    pricingCardFeaturesClass,
    pricingCardFeatureClass,
    pricingCardButtonClass,
    pricingCardPopularTagClass
  ]));
  
  // Generate IDs for elements
  const sectionId = Date.now();
  const containerId = Date.now() + 1;
  
  // Header elements
  const headerId = Date.now() + 2;
  const titleId = Date.now() + 3;
  const subtitleId = Date.now() + 4;
  const toggleId = Date.now() + 5;
  const monthlyToggleId = Date.now() + 6;
  const yearlyToggleId = Date.now() + 7;
  
  // Pricing cards container
  const cardsContainerId = Date.now() + 8;
  
  // Basic pricing card
  const basicCardId = Date.now() + 9;
  const basicHeaderId = Date.now() + 10;
  const basicTitleId = Date.now() + 11;
  const basicPriceId = Date.now() + 12;
  const basicPeriodId = Date.now() + 13;
  const basicFeaturesId = Date.now() + 14;
  const basicFeature1Id = Date.now() + 15;
  const basicFeature2Id = Date.now() + 16;
  const basicFeature3Id = Date.now() + 17;
  const basicFeature4Id = Date.now() + 18;
  const basicButtonId = Date.now() + 19;
  
  // Pro pricing card
  const proCardId = Date.now() + 20;
  const proHeaderId = Date.now() + 21;
  const proTitleId = Date.now() + 22;
  const proPriceId = Date.now() + 23;
  const proPeriodId = Date.now() + 24;
  const proFeaturesId = Date.now() + 25;
  const proFeature1Id = Date.now() + 26;
  const proFeature2Id = Date.now() + 27;
  const proFeature3Id = Date.now() + 28;
  const proFeature4Id = Date.now() + 29;
  const proFeature5Id = Date.now() + 30;
  const proButtonId = Date.now() + 31;
  const proPopularTagId = Date.now() + 32;
  
  // Enterprise pricing card
  const enterpriseCardId = Date.now() + 33;
  const enterpriseHeaderId = Date.now() + 34;
  const enterpriseTitleId = Date.now() + 35;
  const enterprisePriceId = Date.now() + 36;
  const enterprisePeriodId = Date.now() + 37;
  const enterpriseFeaturesId = Date.now() + 38;
  const enterpriseFeature1Id = Date.now() + 39;
  const enterpriseFeature2Id = Date.now() + 40;
  const enterpriseFeature3Id = Date.now() + 41;
  const enterpriseFeature4Id = Date.now() + 42;
  const enterpriseFeature5Id = Date.now() + 43;
  const enterpriseFeature6Id = Date.now() + 44;
  const enterpriseButtonId = Date.now() + 45;
  
  // Feature icons
  const basicIcon1Id = Date.now() + 46;
  const basicIcon2Id = Date.now() + 47;
  const basicIcon3Id = Date.now() + 48;
  const basicIcon4Id = Date.now() + 49;
  
  const proIcon1Id = Date.now() + 50;
  const proIcon2Id = Date.now() + 51;
  const proIcon3Id = Date.now() + 52;
  const proIcon4Id = Date.now() + 53;
  const proIcon5Id = Date.now() + 54;
  
  const enterpriseIcon1Id = Date.now() + 55;
  const enterpriseIcon2Id = Date.now() + 56;
  const enterpriseIcon3Id = Date.now() + 57;
  const enterpriseIcon4Id = Date.now() + 58;
  const enterpriseIcon5Id = Date.now() + 59;
  const enterpriseIcon6Id = Date.now() + 60;

  const newElements = [
    // Main Pricing Section
    {
      id: sectionId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'section',
      name: 'Pricing Section',
      parentId: null,
      className: pricingSectionClass,
      styles: {},
      children: [containerId],
      text: '',
      attributes: {
        'data-element': 'pricing-section',
        'data-component': 'section',
        'data-section-type': 'pricing',
        'data-page-id': currentPageId
      }
    },
    
    // Container
    {
      id: containerId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Pricing Container',
      parentId: sectionId,
      hierarchyName: getHierarchyName('div'),
      className: pricingContainerClass,
      styles: {},
      children: [headerId, cardsContainerId],
      text: '',
      attributes: {
        'data-element': 'pricing-container'
      }
    },
    
    // Header Section
    {
      id: headerId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Pricing Header',
      parentId: containerId,
      hierarchyName: getHierarchyName('div'),
      className: pricingHeaderClass,
      styles: {},
      children: [titleId, subtitleId, toggleId],
      text: '',
      attributes: {
        'data-element': 'pricing-header'
      }
    },
    
    // Title 
    {
      id: titleId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'h2',
      name: 'Pricing Title',
      parentId: headerId,
      hierarchyName: getHierarchyName('h2'),
      className: pricingTitleClass,
      styles: {},
      children: [],
      text: 'Pricing Plans for Every Need',
      attributes: {
        'data-element': 'pricing-title'
      }
    },
    
    // Subtitle
    {
      id: subtitleId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'p',
      name: 'Pricing Subtitle',
      parentId: headerId,
      hierarchyName: getHierarchyName('p'),
      className: pricingSubtitleClass,
      styles: {},
      children: [],
      text: 'Choose the plan that works best for your business needs. All plans come with a 14-day free trial.',
      attributes: {
        'data-element': 'pricing-subtitle'
      }
    },
    
    // Toggle Container
    {
      id: toggleId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Pricing Toggle',
      parentId: headerId,
      hierarchyName: getHierarchyName('div'),
      className: pricingToggleClass,
      styles: {},
      children: [monthlyToggleId, yearlyToggleId],
      text: '',
      attributes: {
        'data-element': 'pricing-toggle'
      }
    },
    
    // Monthly Toggle Button
    {
      id: monthlyToggleId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'button',
      name: 'Monthly Toggle',
      parentId: toggleId,
      hierarchyName: getHierarchyName('button'),
      className: `${pricingToggleButtonClass} ${pricingToggleActiveClass}`,
      styles: {},
      children: [],
      text: 'Monthly',
      attributes: {
        'data-element': 'pricing-toggle-button',
        'data-toggle': 'monthly'
      }
    },
    
    // Yearly Toggle Button
    {
      id: yearlyToggleId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'button',
      name: 'Yearly Toggle',
      parentId: toggleId,
      hierarchyName: getHierarchyName('button'),
      className: pricingToggleButtonClass,
      styles: {},
      children: [],
      text: 'Yearly (Save 20%)',
      attributes: {
        'data-element': 'pricing-toggle-button',
        'data-toggle': 'yearly'
      }
    },
    
    // Pricing Cards Container
    {
      id: cardsContainerId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Pricing Cards',
      parentId: containerId,
      hierarchyName: getHierarchyName('div'),
      className: pricingCardsClass,
      styles: {},
      children: [basicCardId, proCardId, enterpriseCardId],
      text: '',
      attributes: {
        'data-element': 'pricing-cards'
      }
    },
    
    // Basic Card
    {
      id: basicCardId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Basic Card',
      parentId: cardsContainerId,
      hierarchyName: getHierarchyName('div'),
      className: pricingCardClass,
      styles: {},
      children: [basicHeaderId, basicFeaturesId, basicButtonId],
      text: '',
      attributes: {
        'data-element': 'pricing-card',
        'data-plan': 'basic'
      }
    },
    
    // Basic Card Header
    {
      id: basicHeaderId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Basic Card Header',
      parentId: basicCardId,
      hierarchyName: getHierarchyName('div'),
      className: pricingCardHeaderClass,
      styles: {},
      children: [basicTitleId, basicPriceId, basicPeriodId],
      text: '',
      attributes: {
        'data-element': 'pricing-card-header'
      }
    },
    
    // Basic Card Title
    {
      id: basicTitleId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'h3',
      name: 'Basic Card Title',
      parentId: basicHeaderId,
      hierarchyName: getHierarchyName('h3'),
      className: pricingCardTitleClass,
      styles: {},
      children: [],
      text: 'Basic',
      attributes: {
        'data-element': 'pricing-card-title'
      }
    },
    
    // Basic Card Price
    {
      id: basicPriceId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Basic Card Price',
      parentId: basicHeaderId,
      hierarchyName: getHierarchyName('div'),
      className: pricingCardPriceClass,
      styles: {},
      children: [],
      text: '$19',
      attributes: {
        'data-element': 'pricing-card-price'
      }
    },
    
    // Basic Card Period
    {
      id: basicPeriodId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Basic Card Period',
      parentId: basicHeaderId,
      hierarchyName: getHierarchyName('div'),
      className: pricingCardPeriodClass,
      styles: {},
      children: [],
      text: 'per month',
      attributes: {
        'data-element': 'pricing-card-period'
      }
    },
    
    // Basic Card Features
    {
      id: basicFeaturesId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Basic Card Features',
      parentId: basicCardId,
      hierarchyName: getHierarchyName('div'),
      className: pricingCardFeaturesClass,
      styles: {},
      children: [basicFeature1Id, basicFeature2Id, basicFeature3Id, basicFeature4Id],
      text: '',
      attributes: {
        'data-element': 'pricing-card-features'
      }
    },
    
    // Basic Feature 1
    {
      id: basicFeature1Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Basic Feature 1',
      parentId: basicFeaturesId,
      hierarchyName: getHierarchyName('div'),
      className: pricingCardFeatureClass,
      styles: {},
      children: [basicIcon1Id],
      text: '1 Website',
      attributes: {
        'data-element': 'pricing-card-feature'
      }
    },
    
    // Basic Feature 1 Icon
    {
      id: basicIcon1Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'image',
      name: 'Basic Feature 1 Icon',
      parentId: basicFeature1Id,
      hierarchyName: getHierarchyName('image'),
      styles: {
        width: '16px',
        height: '16px'
      },
      src: 'https://api.iconify.design/material-symbols:check-circle-outline.svg?color=%234361ee',
      attributes: {
        'data-element': 'feature-icon',
        'alt': 'Check Icon'
      }
    },
    
    // Basic Feature 2
    {
      id: basicFeature2Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Basic Feature 2',
      parentId: basicFeaturesId,
      hierarchyName: getHierarchyName('div'),
      className: pricingCardFeatureClass,
      styles: {},
      children: [basicIcon2Id],
      text: '10GB Storage',
      attributes: {
        'data-element': 'pricing-card-feature'
      }
    },
    
    // Basic Feature 2 Icon
    {
      id: basicIcon2Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'image',
      name: 'Basic Feature 2 Icon',
      parentId: basicFeature2Id,
      hierarchyName: getHierarchyName('image'),
      styles: {
        width: '16px',
        height: '16px'
      },
      src: 'https://api.iconify.design/material-symbols:check-circle-outline.svg?color=%234361ee',
      attributes: {
        'data-element': 'feature-icon',
        'alt': 'Check Icon'
      }
    },
    
    // Basic Feature 3
    {
      id: basicFeature3Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Basic Feature 3',
      parentId: basicFeaturesId,
      hierarchyName: getHierarchyName('div'),
      className: pricingCardFeatureClass,
      styles: {},
      children: [basicIcon3Id],
      text: '10 Email Accounts',
      attributes: {
        'data-element': 'pricing-card-feature'
      }
    },
    
    // Basic Feature 3 Icon
    {
      id: basicIcon3Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'image',
      name: 'Basic Feature 3 Icon',
      parentId: basicFeature3Id,
      hierarchyName: getHierarchyName('image'),
      styles: {
        width: '16px',
        height: '16px'
      },
      src: 'https://api.iconify.design/material-symbols:check-circle-outline.svg?color=%234361ee',
      attributes: {
        'data-element': 'feature-icon',
        'alt': 'Check Icon'
      }
    },
    
    // Basic Feature 4
    {
      id: basicFeature4Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Basic Feature 4',
      parentId: basicFeaturesId,
      hierarchyName: getHierarchyName('div'),
      className: pricingCardFeatureClass,
      styles: {},
      children: [basicIcon4Id],
      text: 'Basic Support',
      attributes: {
        'data-element': 'pricing-card-feature'
      }
    },
    
    // Basic Feature 4 Icon
    {
      id: basicIcon4Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'image',
      name: 'Basic Feature 4 Icon',
      parentId: basicFeature4Id,
      hierarchyName: getHierarchyName('image'),
      styles: {
        width: '16px',
        height: '16px'
      },
      src: 'https://api.iconify.design/material-symbols:check-circle-outline.svg?color=%234361ee',
      attributes: {
        'data-element': 'feature-icon',
        'alt': 'Check Icon'
      }
    },
    
    // Basic Card Button
    {
      id: basicButtonId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'button',
      name: 'Basic Card Button',
      parentId: basicCardId,
      hierarchyName: getHierarchyName('button'),
      className: pricingCardButtonClass,
      styles: {
        backgroundColor: '#ffffff',
        color: '#4361ee',
        border: '2px solid #4361ee'
      },
      children: [],
      text: 'Get Started',
      attributes: {
        'data-element': 'pricing-card-button'
      }
    },
    
    // Pro Card
    {
      id: proCardId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Pro Card',
      parentId: cardsContainerId,
      hierarchyName: getHierarchyName('div'),
      className: `${pricingCardClass} ${pricingCardPopularClass}`,
      styles: {},
      children: [proHeaderId, proFeaturesId, proButtonId, proPopularTagId],
      text: '',
      attributes: {
        'data-element': 'pricing-card',
        'data-plan': 'pro'
      }
    },
    
    // Pro Card Header
    {
      id: proHeaderId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Pro Card Header',
      parentId: proCardId,
      hierarchyName: getHierarchyName('div'),
      className: pricingCardHeaderClass,
      styles: {},
      children: [proTitleId, proPriceId, proPeriodId],
      text: '',
      attributes: {
        'data-element': 'pricing-card-header'
      }
    },
    
    // Pro Card Title
    {
      id: proTitleId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'h3',
      name: 'Pro Card Title',
      parentId: proHeaderId,
      hierarchyName: getHierarchyName('h3'),
      className: pricingCardTitleClass,
      styles: {},
      children: [],
      text: 'Pro',
      attributes: {
        'data-element': 'pricing-card-title'
      }
    },
    
    // Pro Card Price
    {
      id: proPriceId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Pro Card Price',
      parentId: proHeaderId,
      hierarchyName: getHierarchyName('div'),
      className: pricingCardPriceClass,
      styles: {},
      children: [],
      text: '$49',
      attributes: {
        'data-element': 'pricing-card-price'
      }
    },
    
    // Pro Card Period
    {
      id: proPeriodId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Pro Card Period',
      parentId: proHeaderId,
      hierarchyName: getHierarchyName('div'),
      className: pricingCardPeriodClass,
      styles: {},
      children: [],
      text: 'per month',
      attributes: {
        'data-element': 'pricing-card-period'
      }
    },
    
    // Pro Card Features
    {
      id: proFeaturesId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Pro Card Features',
      parentId: proCardId,
      hierarchyName: getHierarchyName('div'),
      className: pricingCardFeaturesClass,
      styles: {},
      children: [proFeature1Id, proFeature2Id, proFeature3Id, proFeature4Id, proFeature5Id],
      text: '',
      attributes: {
        'data-element': 'pricing-card-features'
      }
    },
    
    // Pro Feature 1
    {
      id: proFeature1Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Pro Feature 1',
      parentId: proFeaturesId,
      hierarchyName: getHierarchyName('div'),
      className: pricingCardFeatureClass,
      styles: {},
      children: [proIcon1Id],
      text: '5 Websites',
      attributes: {
        'data-element': 'pricing-card-feature'
      }
    },
    
    // Pro Feature 1 Icon
    {
      id: proIcon1Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'image',
      name: 'Pro Feature 1 Icon',
      parentId: proFeature1Id,
      hierarchyName: getHierarchyName('image'),
      styles: {
        width: '16px',
        height: '16px'
      },
      src: 'https://api.iconify.design/material-symbols:check-circle-outline.svg?color=%234361ee',
      attributes: {
        'data-element': 'feature-icon',
        'alt': 'Check Icon'
      }
    },
    
    // Pro Feature 2
    {
      id: proFeature2Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Pro Feature 2',
      parentId: proFeaturesId,
      hierarchyName: getHierarchyName('div'),
      className: pricingCardFeatureClass,
      styles: {},
      children: [proIcon2Id],
      text: '50GB Storage',
      attributes: {
        'data-element': 'pricing-card-feature'
      }
    },
    
    // Pro Feature 2 Icon
    {
      id: proIcon2Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'image',
      name: 'Pro Feature 2 Icon',
      parentId: proFeature2Id,
      hierarchyName: getHierarchyName('image'),
      styles: {
        width: '16px',
        height: '16px'
      },
      src: 'https://api.iconify.design/material-symbols:check-circle-outline.svg?color=%234361ee',
      attributes: {
        'data-element': 'feature-icon',
        'alt': 'Check Icon'
      }
    },
    
    // Pro Feature 3
    {
      id: proFeature3Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Pro Feature 3',
      parentId: proFeaturesId,
      hierarchyName: getHierarchyName('div'),
      className: pricingCardFeatureClass,
      styles: {},
      children: [proIcon3Id],
      text: '50 Email Accounts',
      attributes: {
        'data-element': 'pricing-card-feature'
      }
    },
    
    // Pro Feature 3 Icon
    {
      id: proIcon3Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'image',
      name: 'Pro Feature 3 Icon',
      parentId: proFeature3Id,
      hierarchyName: getHierarchyName('image'),
      styles: {
        width: '16px',
        height: '16px'
      },
      src: 'https://api.iconify.design/material-symbols:check-circle-outline.svg?color=%234361ee',
      attributes: {
        'data-element': 'feature-icon',
        'alt': 'Check Icon'
      }
    },
    
    // Pro Feature 4
    {
      id: proFeature4Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Pro Feature 4',
      parentId: proFeaturesId,
      hierarchyName: getHierarchyName('div'),
      className: pricingCardFeatureClass,
      styles: {},
      children: [proIcon4Id],
      text: 'Priority Support',
      attributes: {
        'data-element': 'pricing-card-feature'
      }
    },
    
    // Pro Feature 4 Icon
    {
      id: proIcon4Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'image',
      name: 'Pro Feature 4 Icon',
      parentId: proFeature4Id,
      hierarchyName: getHierarchyName('image'),
      styles: {
        width: '16px',
        height: '16px'
      },
      src: 'https://api.iconify.design/material-symbols:check-circle-outline.svg?color=%234361ee',
      attributes: {
        'data-element': 'feature-icon',
        'alt': 'Check Icon'
      }
    },
    
    // Pro Feature 5
    {
      id: proFeature5Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Pro Feature 5',
      parentId: proFeaturesId,
      hierarchyName: getHierarchyName('div'),
      className: pricingCardFeatureClass,
      styles: {},
      children: [proIcon5Id],
      text: 'Advanced Analytics',
      attributes: {
        'data-element': 'pricing-card-feature'
      }
    },
    
    // Pro Feature 5 Icon
    {
      id: proIcon5Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'image',
      name: 'Pro Feature 5 Icon',
      parentId: proFeature5Id,
      hierarchyName: getHierarchyName('image'),
      styles: {
        width: '16px',
        height: '16px'
      },
      src: 'https://api.iconify.design/material-symbols:check-circle-outline.svg?color=%234361ee',
      attributes: {
        'data-element': 'feature-icon',
        'alt': 'Check Icon'
      }
    },
    
    // Pro Card Button
    {
      id: proButtonId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'button',
      name: 'Pro Card Button',
      parentId: proCardId,
      hierarchyName: getHierarchyName('button'),
      className: pricingCardButtonClass,
      styles: {
        backgroundColor: '#4361ee',
        color: '#ffffff',
        border: '2px solid #4361ee'
      },
      children: [],
      text: 'Get Started',
      attributes: {
        'data-element': 'pricing-card-button'
      }
    },
    
    // Pro Card Popular Tag
    {
      id: proPopularTagId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Popular Tag',
      parentId: proCardId,
      hierarchyName: getHierarchyName('div'),
      className: pricingCardPopularTagClass,
      styles: {},
      children: [],
      text: 'POPULAR',
      attributes: {
        'data-element': 'pricing-card-popular-tag'
      }
    },
    
    // Enterprise Card
    {
      id: enterpriseCardId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Enterprise Card',
      parentId: cardsContainerId,
      hierarchyName: getHierarchyName('div'),
      className: pricingCardClass,
      styles: {},
      children: [enterpriseHeaderId, enterpriseFeaturesId, enterpriseButtonId],
      text: '',
      attributes: {
        'data-element': 'pricing-card',
        'data-plan': 'enterprise'
      }
    },
    
    // Enterprise Card Header
    {
      id: enterpriseHeaderId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Enterprise Card Header',
      parentId: enterpriseCardId,
      hierarchyName: getHierarchyName('div'),
      className: pricingCardHeaderClass,
      styles: {},
      children: [enterpriseTitleId, enterprisePriceId, enterprisePeriodId],
      text: '',
      attributes: {
        'data-element': 'pricing-card-header'
      }
    },
    
    // Enterprise Card Title
    {
      id: enterpriseTitleId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'h3',
      name: 'Enterprise Card Title',
      parentId: enterpriseHeaderId,
      hierarchyName: getHierarchyName('h3'),
      className: pricingCardTitleClass,
      styles: {},
      children: [],
      text: 'Enterprise',
      attributes: {
        'data-element': 'pricing-card-title'
      }
    },
    
    // Enterprise Card Price
    {
      id: enterprisePriceId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Enterprise Card Price',
      parentId: enterpriseHeaderId,
      hierarchyName: getHierarchyName('div'),
      className: pricingCardPriceClass,
      styles: {},
      children: [],
      text: '$99',
      attributes: {
        'data-element': 'pricing-card-price'
      }
    },
    
    // Enterprise Card Period
    {
      id: enterprisePeriodId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Enterprise Card Period',
      parentId: enterpriseHeaderId,
      hierarchyName: getHierarchyName('div'),
      className: pricingCardPeriodClass,
      styles: {},
      children: [],
      text: 'per month',
      attributes: {
        'data-element': 'pricing-card-period'
      }
    },
    
    // Enterprise Card Features
    {
      id: enterpriseFeaturesId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Enterprise Card Features',
      parentId: enterpriseCardId,
      hierarchyName: getHierarchyName('div'),
      className: pricingCardFeaturesClass,
      styles: {},
      children: [
        enterpriseFeature1Id, 
        enterpriseFeature2Id, 
        enterpriseFeature3Id, 
        enterpriseFeature4Id, 
        enterpriseFeature5Id,
        enterpriseFeature6Id
      ],
      text: '',
      attributes: {
        'data-element': 'pricing-card-features'
      }
    },
    
    // Enterprise Feature 1
    {
      id: enterpriseFeature1Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Enterprise Feature 1',
      parentId: enterpriseFeaturesId,
      hierarchyName: getHierarchyName('div'),
      className: pricingCardFeatureClass,
      styles: {},
      children: [enterpriseIcon1Id],
      text: 'Unlimited Websites',
      attributes: {
        'data-element': 'pricing-card-feature'
      }
    },
    
    // Enterprise Feature 1 Icon
    {
      id: enterpriseIcon1Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'image',
      name: 'Enterprise Feature 1 Icon',
      parentId: enterpriseFeature1Id,
      hierarchyName: getHierarchyName('image'),
      styles: {
        width: '16px',
        height: '16px'
      },
      src: 'https://api.iconify.design/material-symbols:check-circle-outline.svg?color=%234361ee',
      attributes: {
        'data-element': 'feature-icon',
        'alt': 'Check Icon'
      }
    },
    
    // Enterprise Feature 2
    {
      id: enterpriseFeature2Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Enterprise Feature 2',
      parentId: enterpriseFeaturesId,
      hierarchyName: getHierarchyName('div'),
      className: pricingCardFeatureClass,
      styles: {},
      children: [enterpriseIcon2Id],
      text: '500GB Storage',
      attributes: {
        'data-element': 'pricing-card-feature'
      }
    },
    
    // Enterprise Feature 2 Icon
    {
      id: enterpriseIcon2Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'image',
      name: 'Enterprise Feature 2 Icon',
      parentId: enterpriseFeature2Id,
      hierarchyName: getHierarchyName('image'),
      styles: {
        width: '16px',
        height: '16px'
      },
      src: 'https://api.iconify.design/material-symbols:check-circle-outline.svg?color=%234361ee',
      attributes: {
        'data-element': 'feature-icon',
        'alt': 'Check Icon'
      }
    },
    
    // Enterprise Feature 3
    {
      id: enterpriseFeature3Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Enterprise Feature 3',
      parentId: enterpriseFeaturesId,
      hierarchyName: getHierarchyName('div'),
      className: pricingCardFeatureClass,
      styles: {},
      children: [enterpriseIcon3Id],
      text: 'Unlimited Email Accounts',
      attributes: {
        'data-element': 'pricing-card-feature'
      }
    },
    
    // Enterprise Feature 3 Icon
    {
      id: enterpriseIcon3Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'image',
      name: 'Enterprise Feature 3 Icon',
      parentId: enterpriseFeature3Id,
      hierarchyName: getHierarchyName('image'),
      styles: {
        width: '16px',
        height: '16px'
      },
      src: 'https://api.iconify.design/material-symbols:check-circle-outline.svg?color=%234361ee',
      attributes: {
        'data-element': 'feature-icon',
        'alt': 'Check Icon'
      }
    },
    
    // Enterprise Feature 4
    {
      id: enterpriseFeature4Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Enterprise Feature 4',
      parentId: enterpriseFeaturesId,
      hierarchyName: getHierarchyName('div'),
      className: pricingCardFeatureClass,
      styles: {},
      children: [enterpriseIcon4Id],
      text: '24/7 Dedicated Support',
      attributes: {
        'data-element': 'pricing-card-feature'
      }
    },
    
    // Enterprise Feature 4 Icon
    {
      id: enterpriseIcon4Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'image',
      name: 'Enterprise Feature 4 Icon',
      parentId: enterpriseFeature4Id,
      hierarchyName: getHierarchyName('image'),
      styles: {
        width: '16px',
        height: '16px'
      },
      src: 'https://api.iconify.design/material-symbols:check-circle-outline.svg?color=%234361ee',
      attributes: {
        'data-element': 'feature-icon',
        'alt': 'Check Icon'
      }
    },
    
    // Enterprise Feature 5
    {
      id: enterpriseFeature5Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Enterprise Feature 5',
      parentId: enterpriseFeaturesId,
      hierarchyName: getHierarchyName('div'),
      className: pricingCardFeatureClass,
      styles: {},
      children: [enterpriseIcon5Id],
      text: 'Advanced Analytics',
      attributes: {
        'data-element': 'pricing-card-feature'
      }
    },
    
    // Enterprise Feature 5 Icon
    {
      id: enterpriseIcon5Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'image',
      name: 'Enterprise Feature 5 Icon',
      parentId: enterpriseFeature5Id,
      hierarchyName: getHierarchyName('image'),
      styles: {
        width: '16px',
        height: '16px'
      },
      src: 'https://api.iconify.design/material-symbols:check-circle-outline.svg?color=%234361ee',
      attributes: {
        'data-element': 'feature-icon',
        'alt': 'Check Icon'
      }
    },
    
    // Enterprise Feature 6
    {
      id: enterpriseFeature6Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Enterprise Feature 6',
      parentId: enterpriseFeaturesId,
      hierarchyName: getHierarchyName('div'),
      className: pricingCardFeatureClass,
      styles: {},
      children: [enterpriseIcon6Id],
      text: 'Custom Integrations',
      attributes: {
        'data-element': 'pricing-card-feature'
      }
    },
    
    // Enterprise Feature 6 Icon
    {
      id: enterpriseIcon6Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'image',
      name: 'Enterprise Feature 6 Icon',
      parentId: enterpriseFeature6Id,
      hierarchyName: getHierarchyName('image'),
      styles: {
        width: '16px',
        height: '16px'
      },
      src: 'https://api.iconify.design/material-symbols:check-circle-outline.svg?color=%234361ee',
      attributes: {
        'data-element': 'feature-icon',
        'alt': 'Check Icon'
      }
    },
    
    // Enterprise Card Button
    {
      id: enterpriseButtonId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'button',
      name: 'Enterprise Card Button',
      parentId: enterpriseCardId,
      hierarchyName: getHierarchyName('button'),
      className: pricingCardButtonClass,
      styles: {
        backgroundColor: '#ffffff',
        color: '#4361ee',
        border: '2px solid #4361ee'
      },
      children: [],
      text: 'Contact Us',
      attributes: {
        'data-element': 'pricing-card-button'
      }
    }
  ];
  
  return {
    pricingSection: newElements[0],
    elements: newElements
  };
};

export default createPricingElements; 